import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, LogIn, Sparkles, HelpCircle, Inbox, Link2, Trash2, Infinity as InfinityIcon } from 'lucide-react';
import { api, timeAgo } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

function formatExpiresAt(iso) {
  if (!iso) return { label: 'Never', soon: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { label: 'Expired', soon: true };
  const sec = Math.floor(ms / 1000);
  if (sec < 3600) return { label: `${Math.floor(sec / 60)}m`, soon: true };
  if (sec < 86400) return { label: `${Math.floor(sec / 3600)}h`, soon: sec < 7200 };
  if (sec < 604800) return { label: `${Math.floor(sec / 86400)}d`, soon: false };
  return { label: `${Math.floor(sec / 604800)}w`, soon: false };
}

export default function Invites() {
  const { guildId } = useGuild();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setData(null);
    setActive(null);
    api.invites(guildId).then(setData).catch(err => setData({ error: err.message }));
    api.activeInvites(guildId).then(setActive).catch(err => setActive({ error: err.message }));
  }, [guildId]);

  async function revoke(code) {
    if (!confirm(`Revoke invite "${code}"? This cannot be undone.`)) return;
    try {
      await api.revokeInvite(guildId, code);
      setActive(a => ({ ...a, invites: a.invites.filter(i => i.code !== code) }));
      setToast({ type: 'success', msg: `Revoked ${code}` });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setTimeout(() => setToast(null), 2500);
    }
  }

  if (!data) return (
    <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>
  );
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  const { counts, leaderboard, recent } = data;

  return (
    <>
      <h1>Invites</h1>
      <p className="subtitle">Who invited whom. Built from a per-guild invite-uses cache, diffed on every join.</p>

      <div className="stat-grid">
        <div className="stat info">
          <div className="label">Tracked joins</div>
          <div className="value">{counts.total}</div>
          <div className="delta">since SoupOS started watching</div>
        </div>
        <div className="stat success">
          <div className="label">Attributed</div>
          <div className="value">{counts.attributed}</div>
          <div className="delta">have a known inviter</div>
        </div>
        <div className="stat warn">
          <div className="label">Vanity URL</div>
          <div className="value">{counts.vanity ?? 0}</div>
          <div className="delta">joined via <span className="tag">discord.gg/&lt;name&gt;</span></div>
        </div>
        <div className="stat">
          <div className="label">Unknown</div>
          <div className="value">{counts.unknown ?? 0}</div>
          <div className="delta">bot offline / direct join</div>
        </div>
      </div>

      <div className="split">
        <div>
          <h3>Recent joins</h3>
          {!recent.length ? (
            <div className="card empty"><div className="ico"><Inbox size={32} strokeWidth={1.5} /></div><p>No joins tracked yet.</p></div>
          ) : (
            <div className="card activity">
              {recent.map(r => (
                <div className="row-item" key={r.id}>
                  <div className="ico success"><LogIn size={16} strokeWidth={2} /></div>
                  <div className="text">
                    <div className="head">
                      <Link to={`/g/${guildId}/users/${r.invitee_id}`} className="tag">{r.invitee_id}</Link>
                      {' '}joined via{' '}
                      {r.is_vanity
                        ? <span className="badge accent">vanity URL</span>
                        : r.inviter_id
                          ? <><Link to={`/g/${guildId}/users/${r.inviter_id}`} className="tag">{r.inviter_id}</Link>{r.invite_code && <span className="tag" style={{ marginLeft: 6 }}>{r.invite_code}</span>}</>
                          : <span className="badge"><HelpCircle size={11} strokeWidth={2} style={{ verticalAlign: 'middle', marginRight: 3 }} />unknown</span>}
                    </div>
                  </div>
                  <div className="when">{timeAgo(r.joined_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <h3>Active invites</h3>
          {!active ? (
            <div className="card"><div className="skel skel-line med" /><div className="skel skel-line short" /></div>
          ) : active.error ? (
            <div className="card empty"><p>Could not load: {active.error}</p></div>
          ) : !active.invites.length && !active.vanity ? (
            <div className="card empty"><div className="ico"><Link2 size={32} strokeWidth={1.5} /></div><p>No active invites.</p></div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Channel</th>
                    <th>Made by</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Uses</th>
                    <th>Expires</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {active.vanity && (
                    <tr>
                      <td><span className="tag">{active.vanity.code}</span> <span className="badge accent" style={{ marginLeft: 4 }}>vanity</span></td>
                      <td className="dim">—</td>
                      <td className="dim">—</td>
                      <td>{active.vanity.uses} / <InfinityIcon size={12} strokeWidth={2} style={{ verticalAlign: 'middle' }} /></td>
                      <td className="dim">Never</td>
                      <td className="dim" style={{ fontSize: 12 }}>via boost perk</td>
                    </tr>
                  )}
                  {active.invites.map(inv => {
                    const exp = formatExpiresAt(inv.expires_at);
                    return (
                      <tr key={inv.code}>
                        <td><a href={inv.url} target="_blank" rel="noreferrer" className="tag">{inv.code}</a></td>
                        <td>{inv.channel ? `#${inv.channel.name}` : <span className="dim">—</span>}</td>
                        <td>{inv.inviter ? <Link className="tag" to={`/g/${guildId}/users/${inv.inviter.id}`}>{inv.inviter.username}</Link> : <span className="dim">—</span>}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {inv.uses} / {inv.max_uses === 0 ? <InfinityIcon size={12} strokeWidth={2} style={{ verticalAlign: 'middle' }} /> : inv.max_uses}
                        </td>
                        <td>
                          <span className={exp.soon ? 'badge warn' : ''}>{exp.label}</span>
                        </td>
                        <td>
                          <button className="ghost" title="Revoke" onClick={() => revoke(inv.code)} style={{ padding: '.3rem .55rem' }}>
                            <Trash2 size={13} strokeWidth={2} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <h3 style={{ marginTop: '2rem' }}>Top inviters</h3>
          {!leaderboard.length ? (
            <div className="card empty"><div className="ico"><Trophy size={32} strokeWidth={1.5} /></div><p>No attributed invites yet.</p></div>
          ) : (
            <div className="card" style={{ padding: 0 }}>
              <table className="table">
                <thead><tr><th style={{ width: 40 }}>#</th><th>User</th><th style={{ textAlign: 'right' }}>Invites</th></tr></thead>
                <tbody>
                  {leaderboard.map((row, i) => (
                    <tr key={row.inviter_id}>
                      <td>{i === 0 ? <Sparkles size={16} strokeWidth={2} style={{ color: 'var(--warn)' }} /> : i + 1}</td>
                      <td><Link to={`/g/${guildId}/users/${row.inviter_id}`} className="tag">{row.inviter_id}</Link></td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
